import React, { useCallback, useEffect, useState } from 'react';
import { api, getMe } from '../lib/api';
import Icon from '../components/Icon';
import { ReplyQuote, ReplyingTo } from '../components/ReplyQuote';
import { AttachButton, ChatAttachment, PendingFile, uploadChatFile, useFileDrop } from '../components/ChatFile';
import MentionTextarea from '../components/MentionTextarea';
import RichText from '../components/RichText';
import ChatLinkPreview from '../components/ChatLinkPreview';
import Avatar from '../components/Avatar';
import EmojiReactions from '../components/EmojiReactions';
import { openProfile } from '../lib/nav';
import Loader from '../components/Loader';

/* Working groups: a private space where connections work on something
   together — topics, pinned resources, @mentions. You add your connections;
   they join when they accept. */

const PAGE = 50; // the API's page of messages

const when = (iso) => {
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) return `${Math.round(mins / 60)}h`;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

function PickConnections({ exclude = [], picked, setPicked }) {
  const [people, setPeople] = useState(null);
  useEffect(() => {
    api.connections().then((rows) => setPeople(rows.filter((c) => c.active && !exclude.includes(c.other.id))
      .map((c) => c.other))).catch(() => setPeople([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (people === null) return <p className="muted-sm">Loading your connections…</p>;
  if (!people.length) return <p className="muted-sm">No one else to add — groups are for your connections.</p>;
  return (
    <div className="share-people">
      {people.map((p) => (
        <label key={p.id} className={`share-person${picked.includes(p.id) ? ' on' : ''}`}>
          <input type="checkbox" checked={picked.includes(p.id)}
            onChange={() => setPicked((x) => (x.includes(p.id) ? x.filter((i) => i !== p.id) : [...x, p.id]))} />
          <span><b>{p.name}</b>{p.headline ? <em> · {p.headline}</em> : null}</span>
        </label>
      ))}
    </div>
  );
}

function NewGroup({ notify, onCreated, onCancel }) {
  const [name, setName] = useState('');
  const [purpose, setPurpose] = useState('');
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  async function create() {
    setBusy(true);
    try {
      const g = await api.createGroup({ name: name.trim(), purpose: purpose.trim(), invite: picked });
      notify?.(picked.length ? `Group created — ${picked.length} invited` : 'Group created');
      onCreated(g.id);
    } catch (e) { notify?.(e.message); } finally { setBusy(false); }
  }
  return (
    <div className="card pad grp-new">
      <h3>Start a working group</h3>
      <input className="in" maxLength={80} value={name} placeholder="Name — e.g. PH stablecoin on-ramp"
        onChange={(e) => setName(e.target.value)} />
      <textarea className="ta" rows={2} maxLength={300} value={purpose}
        placeholder="What are you working on together? (optional)" onChange={(e) => setPurpose(e.target.value)} />
      <p className="muted-sm">Who to invite (they join when they accept, up to 20 people):</p>
      <PickConnections picked={picked} setPicked={setPicked} />
      <div className="share-actions">
        <button className="btn primary sm" disabled={name.trim().length < 3 || busy} onClick={create}>
          {busy ? 'Creating…' : 'Create group'}
        </button>
        <button className="btn quiet sm" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function Pins({ group, notify, onChanged }) {
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [note, setNote] = useState('');
  const [fileId, setFileId] = useState('');
  const [files, setFiles] = useState(null);
  useEffect(() => {
    if (adding && files === null) api.files().then((d) => setFiles(d.files.filter((f) => f.status === 'ready'))).catch(() => setFiles([]));
  }, [adding, files]);
  async function add() {
    try {
      await api.groupPin(group.id, { title: title.trim(), url: url.trim(), note: note.trim(), file_id: fileId || null });
      setTitle(''); setUrl(''); setNote(''); setFileId(''); setAdding(false);
      onChanged();
    } catch (e) { notify?.(e.message); }
  }
  return (
    <div className="grp-pins">
      <button className="linklike" onClick={() => setOpen(!open)} aria-expanded={open}>
        📌 Pinned ({group.pins.length}) {open ? '▴' : '▾'}
      </button>
      {open && (
        <div className="grp-pin-list">
          {group.pins.length === 0 && <p className="muted-sm">Pin the links, files and notes everyone keeps asking for.</p>}
          {group.pins.map((p) => (
            <div key={p.id} className="grp-pin">
              <span className="grp-pin-kind">{p.kind === 'link' ? '🔗' : p.kind === 'file' ? '📄' : '📝'}</span>
              <div>
                {p.kind === 'link' && <a href={p.url} target="_blank" rel="noopener noreferrer"><b>{p.title}</b></a>}
                {p.kind === 'file' && (
                  <button className="linklike" onClick={() => api.groupPinDownload(group.id, p.id, p.filename || p.title).catch((e) => notify?.(e.message))}>
                    <b>{p.title}</b>
                  </button>
                )}
                {p.kind === 'note' && <b>{p.title}</b>}
                {p.note && <p className="muted-sm grp-pin-note">{p.note}</p>}
                <span className="muted-sm">by {p.by}</span>
              </div>
              {(p.by_id === getMe()?.id || group.my_role === 'owner') && (
                <button className="linklike grp-unpin" aria-label="Unpin"
                  onClick={() => api.groupUnpin(group.id, p.id).then(onChanged).catch((e) => notify?.(e.message))}>Unpin</button>
              )}
            </div>
          ))}
          {adding ? (
            <div className="grp-pin-add">
              <input className="in" maxLength={160} value={title} placeholder="Title" onChange={(e) => setTitle(e.target.value)} />
              <input className="in" value={url} placeholder="Link (https://…) — or leave empty" disabled={Boolean(fileId)}
                onChange={(e) => setUrl(e.target.value)} />
              <select className="in" value={fileId} onChange={(e) => setFileId(e.target.value)} disabled={Boolean(url)}>
                <option value="">— or share one of your files —</option>
                {(files || []).map((f) => <option key={f.id} value={f.id}>{f.filename}</option>)}
              </select>
              <textarea className="ta" rows={2} maxLength={1000} value={note} placeholder="Note (optional)"
                onChange={(e) => setNote(e.target.value)} />
              {fileId && <p className="muted-sm">Pinning a file lets everyone in this group download it.</p>}
              <div className="share-actions">
                <button className="btn primary sm" disabled={!title.trim() || (!url.trim() && !fileId && !note.trim())} onClick={add}>Pin</button>
                <button className="btn quiet sm" onClick={() => setAdding(false)}>Cancel</button>
              </div>
            </div>
          ) : <button className="btn quiet sm" onClick={() => setAdding(true)}>+ Pin something</button>}
        </div>
      )}
    </div>
  );
}

function GroupView({ id, initialTopic, notify, onBack, onChanged }) {
  const [group, setGroup] = useState(null);
  const [topic, setTopic] = useState(initialTopic || null);
  const [msgs, setMsgs] = useState([]);
  // Earlier pages you've loaded; the poll only refreshes the latest page.
  const [older, setOlder] = useState([]);
  const [hasOlder, setHasOlder] = useState(false);
  const [draft, setDraft] = useState('');
  const [replyFocus, setReplyFocus] = useState(0);
  const [replyTo, setReplyTo] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [picked, setPicked] = useState([]);
  const [showPeople, setShowPeople] = useState(false);
  const pick = useCallback(async (file) => {
    if (!file) return;
    setPosting(true);
    const f = await uploadChatFile(file, notify);
    setPosting(false);
    if (f) setPendingFile(f);
  }, [notify]);
  const { over, dropProps, onPaste } = useFileDrop(pick);

  const load = useCallback(() => api.group(id).then((g) => {
    setGroup(g);
    setTopic((t) => (t && g.topics.some((x) => x.id === t) ? t : g.topics[0]?.id));
  }).catch((e) => { notify?.(e.message); onBack(); }), [id, notify, onBack]);
  const loadMsgs = useCallback(() => {
    if (!topic) return Promise.resolve();
    return api.groupMessages(id, topic).then((rows) => {
      setMsgs(rows);
      setHasOlder((h) => h || rows.length === PAGE);
    }).catch(() => {});
  }, [id, topic]);
  useEffect(() => { setOlder([]); setHasOlder(false); }, [topic]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    loadMsgs().then(() => { onChanged?.(); });
    // The group too, so the other topics' unread badges stay current.
    const t = setInterval(() => { loadMsgs(); load(); }, 15000);
    return () => clearInterval(t);
  }, [loadMsgs, load, onChanged]);

  if (!group) return <Loader />;
  const owner = group.my_role === 'owner';
  const loadPeople = () => api.groupPeople(id);

  // An attached file goes to your files first (private, never used for
  // matching), then into the message — only this group's members can open it.
  async function send() {
    if ((!draft.trim() && !pendingFile) || posting) return;
    setPosting(true);
    try {
      const m = await api.groupPost(id, topic, draft.trim(), pendingFile?.id, replyTo?.id);
      setMsgs((x) => [...x, m]);
      setDraft('');
      setReplyTo(null);
      setPendingFile(null);
    } catch (e) { notify?.(e.message); } finally { setPosting(false); }
  }
  const swap = (u) => { const f = (xs) => xs.map((x) => (x.id === u.id ? u : x)); setMsgs(f); setOlder(f); };
  async function loadEarlier() {
    const first = (older[0] || msgs[0])?.id;
    if (!first) return;
    try {
      const rows = await api.groupMessages(id, topic, first);
      setOlder((o) => [...rows, ...o]);
      setHasOlder(rows.length === PAGE);
    } catch (e) { notify?.(e.message); }
  }
  async function closeTopic(t) {
    if (!window.confirm(`Close # ${t.name}? Its messages stay searchable, but nobody can post there.`)) return;
    try { await api.groupCloseTopic(id, t.id); setTopic(null); await load(); } catch (e) { notify?.(e.message); }
  }
  async function addTopic() {
    const name = window.prompt('New topic name (up to 60 characters) — e.g. Specs, Go-to-market');
    if (!name?.trim()) return;
    if (name.trim().length > 60) { notify?.('Topic names can be up to 60 characters.'); return; }
    try { const t = await api.groupAddTopic(id, name.trim()); await load(); setTopic(t.id); } catch (e) { notify?.(e.message); }
  }
  async function invite() {
    try {
      const r = await api.groupInvite(id, picked);
      notify?.(r.invited.length ? `Invited ${r.invited.join(', ')}` : 'Already invited');
      setPicked([]); setInviting(false); load();
    } catch (e) { notify?.(e.message); }
  }
  async function leave() {
    const alone = (group?.members || 0) <= 1;
    if (!window.confirm(owner
      ? (alone ? 'Leave this group? You’re the only member, so it will be archived.' : 'Leave this group? The longest-standing member becomes the owner.')
      : 'Leave this group?')) return;
    try { await api.groupLeave(id); onBack(); } catch (e) { notify?.(e.message); }
  }
  async function archive() {
    if (!window.confirm('Archive this group for everyone? Nobody will be able to open it.')) return;
    try { await api.updateGroup(id, { archived: true }); onBack(); } catch (e) { notify?.(e.message); }
  }

  const active = group.people.filter((p) => p.status === 'active');
  return (
    <div className={`grp-view${over ? ' drop-over' : ''}`} {...dropProps}>
      {over && <div className="drop-hint">Drop to attach to your message</div>}
      <button className="linklike" onClick={onBack}>← All groups</button>
      <header className="grp-head">
        <div>
          <h2>{group.name}</h2>
          {group.purpose && <p className="muted-sm">{group.purpose}</p>}
          <button className="linklike muted-sm" onClick={() => setShowPeople(!showPeople)}>
            {active.length} member{active.length !== 1 ? 's' : ''}
            {group.people.length > active.length ? ` · ${group.people.length - active.length} invited` : ''} {showPeople ? '▴' : '▾'}
          </button>
        </div>
        <div className="grp-head-actions">
          <button className="btn ghost sm" onClick={() => setInviting(!inviting)}>Invite</button>
          <button className="btn quiet sm" onClick={leave}>Leave</button>
          {owner && <button className="btn quiet sm" onClick={archive}>Archive</button>}
        </div>
      </header>
      {showPeople && (
        <ul className="grp-people">
          {group.people.map((p) => (
            <li key={p.person.id}>
              <button type="button" className="person-link name" onClick={() => openProfile(p.person.handle)}><b>{p.person.name}</b></button> <span className="muted-sm">{p.role === 'owner' ? 'owner' : p.status === 'invited' ? 'invited' : ''}</span>
              {owner && p.role !== 'owner' && (
                <button className="linklike" onClick={() => window.confirm(`Remove ${p.person.name} from the group?`)
                  && api.groupRemove(id, p.person.id).then(load).catch((e) => notify?.(e.message))}>remove</button>
              )}
            </li>
          ))}
        </ul>
      )}
      {inviting && (
        <div className="card pad">
          <PickConnections exclude={group.people.map((p) => p.person.id)} picked={picked} setPicked={setPicked} />
          <div className="share-actions">
            <button className="btn primary sm" disabled={!picked.length} onClick={invite}>Invite</button>
            <button className="btn quiet sm" onClick={() => setInviting(false)}>Cancel</button>
          </div>
        </div>
      )}

      <Pins group={group} notify={notify} onChanged={load} />

      <div className="grp-topics" role="tablist">
        {group.topics.map((t) => (
          <button key={t.id} role="tab" aria-selected={t.id === topic} className={`grp-topic${t.id === topic ? ' on' : ''}`}
            onClick={() => setTopic(t.id)}>
            # {t.name}{t.unread > 0 && t.id !== topic ? <em className="grp-unread">{t.unread}</em> : null}
          </button>
        ))}
        <button className="grp-topic add" onClick={addTopic}>+ Topic</button>
        {group.topics.length > 1 && group.topics.find((t) => t.id === topic)?.can_close && (
          <button className="linklike muted-sm" onClick={() => closeTopic(group.topics.find((t) => t.id === topic))}>close this topic</button>
        )}
      </div>

      {/* Newest first, like a feed: you write at the top and what you send appears right there. */}
      <PendingFile file={pendingFile} onRemove={() => setPendingFile(null)} />
      <ReplyingTo to={replyTo} onCancel={() => setReplyTo(null)} />
      <div className="grp-compose">
        <MentionTextarea className="ta" value={draft} loadPeople={loadPeople} rows={2} focusKey={replyFocus}
          placeholder={`Message # ${group.topics.find((t) => t.id === topic)?.name || ''} — type @ to tag someone`}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send(); }}
          onPaste={onPaste} />
        <AttachButton disabled={posting} onPick={pick} />
        <button className="btn primary sm" disabled={(!draft.trim() && !pendingFile) || posting} onClick={() => send()}>
          <Icon name="send" size={16} /> Send
        </button>
      </div>
      <div className="grp-msgs">
        {msgs.length === 0 && <p className="muted-sm">Nothing here yet — start the conversation.</p>}
        {[...older, ...msgs].reverse().map((m) => (
          <div key={m.id} id={`msg-${m.id}`} className={`grp-msg${m.mine ? ' mine' : ''}`}>
            <div className="grp-msg-head">
              <Avatar person={m.author} className="grp-av" />
              <button type="button" className="person-link name" onClick={() => openProfile(m.author?.handle)}><b>{m.author?.name || 'Former member'}</b></button> <span className="muted-sm">{when(m.created_at)}{m.edited ? ' · edited' : ''}</span>
              {m.body !== '(message deleted)' && editingId !== m.id && (
                <span className="fc-c-tools">
                  {!m.mine && m.author?.handle && (
                    <button className="linklike" onClick={() => {
                      const t = `@${m.author.handle} `;
                      const already = new RegExp(`@${m.author.handle}(?![a-z0-9-])`, 'i');
                      setDraft((cur) => (already.test(cur) ? cur : t + cur));
                      setReplyTo(m);
                      setReplyFocus(Date.now());
                    }}>Reply</button>
                  )}
                  {m.mine && (
                    <button className="linklike" onClick={() => { setEditingId(m.id); setEditDraft(m.body); }}>Edit</button>
                  )}
                  {(m.mine || owner) && (
                    <button className="linklike" onClick={() => window.confirm('Delete this message?')
                      && api.groupDeleteMessage(id, m.id).then(loadMsgs).catch((e) => notify?.(e.message))}>Delete</button>
                  )}
                </span>
              )}
            </div>
            {editingId === m.id ? (
              <div className="fc-c-edit">
                <MentionTextarea className="ta" value={editDraft} loadPeople={loadPeople} autoFocus
                  onChange={(e) => setEditDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Escape') setEditingId(null); }} />
                <div className="fc-c-edit-actions">
                  <button className="btn quiet sm" onClick={() => setEditingId(null)}>Cancel</button>
                  <button className="btn primary sm" disabled={!editDraft.trim() && !m.attachment} onClick={() => api.groupEditMessage(id, m.id, editDraft)
                    .then((u) => { swap(u); setEditingId(null); })
                    .catch((e) => notify?.(e.message))}>Save</button>
                </div>
              </div>
            ) : (<>
              {m.body !== '(message deleted)' && <ReplyQuote q={m.reply_to} />}
              {m.body && <div className="grp-msg-body"><RichText text={m.body} mentions={m.mentions} /></div>}
            </>)}
            {m.body && m.body !== '(message deleted)' && <ChatLinkPreview text={m.body} />}
            <ChatAttachment file={m.attachment} mine={m.mine} notify={notify}
              loadImage={() => api.groupImageUrl(id, m.id)}
              download={() => api.groupFileDownload(id, m.id, m.attachment?.filename)}
              save={() => api.groupFileSave(id, m.id)} />
            {m.body !== '(message deleted)' && (
              <EmojiReactions reactions={m.reactions} myId={getMe()?.id} label="this message"
                onReact={(e) => api.groupReact(id, m.id, e)
                  .then((u) => swap(u))
                  .catch((err) => notify?.(err.message))} />
            )}
          </div>
        ))}
        {hasOlder && <button className="linklike muted-sm grp-earlier" onClick={loadEarlier}>Load older messages</button>}
      </div>

    </div>
  );
}

export default function Groups({ notify, openRequest, onChanged }) {
  const [list, setList] = useState(null);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState(openRequest?.id || null);
  const load = useCallback(() => api.groups().then(setList).catch(() => setList([])), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (openRequest?.id) setOpenId(openRequest.id); }, [openRequest]);
  const back = useCallback(() => { setOpenId(null); load(); onChanged?.(); }, [load, onChanged]);

  // Keyed by group (and the topic asked for), so switching groups never shows the last one's messages.
  const askedTopic = openRequest?.id === openId ? openRequest?.topic : undefined;
  if (openId) {
    return <GroupView key={`${openId}:${askedTopic || ''}`} id={openId} initialTopic={askedTopic} notify={notify}
      onBack={back} onChanged={onChanged} />;
  }

  const invites = (list || []).filter((g) => g.my_status === 'invited');
  const mine = (list || []).filter((g) => g.my_status === 'active');
  async function respond(g, yes) {
    try {
      if (yes) { await api.groupAccept(g.id); setOpenId(g.id); } else { await api.groupDecline(g.id); load(); }
      onChanged?.();
    } catch (e) { notify?.(e.message); }
  }
  return (
    <div className="grp-page">
      <div className="grp-title">
        <h1>Groups</h1>
        {!creating && <button className="btn primary sm" onClick={() => setCreating(true)}>+ New group</button>}
      </div>
      <p className="muted-sm">
        Private spaces to work on something together with your connections — topics, pinned resources, and @mentions.
      </p>
      {creating && <NewGroup notify={notify} onCancel={() => setCreating(false)}
        onCreated={(gid) => { setCreating(false); setOpenId(gid); }} />}
      {invites.length > 0 && <h2 className="sec-title">Invitations</h2>}
      {invites.map((g) => (
        <div key={g.id} className="card pad grp-card">
          <div>
            <b>{g.name}</b>
            <p className="muted-sm">{g.invited_by} invited you{g.purpose ? ` · ${g.purpose}` : ''} · {g.members} member{g.members !== 1 ? 's' : ''}</p>
          </div>
          <div className="share-actions">
            <button className="btn primary sm" onClick={() => respond(g, true)}>Join</button>
            <button className="btn quiet sm" onClick={() => respond(g, false)}>No thanks</button>
          </div>
        </div>
      ))}
      {list === null && <Loader />}
      {list !== null && mine.length === 0 && !creating && (
        <div className="card pad">
          <p>No groups yet. Start one for a project, a pilot, or a deal you're working on with a few people.</p>
        </div>
      )}
      {mine.length > 0 && <h2 className="sec-title">Your groups</h2>}
      {mine.map((g) => (
        <button key={g.id} className="card pad grp-card grp-open" onClick={() => setOpenId(g.id)}>
          <div>
            <b>{g.name}</b>
            <p className="muted-sm">{g.purpose || 'Working group'} · {g.members} member{g.members !== 1 ? 's' : ''}</p>
          </div>
          {g.unread > 0 && <em className="grp-unread">{g.unread}</em>}
        </button>
      ))}
    </div>
  );
}
